import { AiSurface } from 'components/ui/overlay-motion';
import { Popover } from 'components/ui/popover';
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalSearchEntityType } from 'types/globalSearch';
import { resolveRecordFromPath } from 'utils/globalSearchRoutes';
import { captureSearchPeekOpened } from 'utils/searchAnalytics';
import { SearchCloseContext } from './GlobalSearchPeekCard';
import { CLOSE_DELAY_MS, PeekPopup } from './RowWithPeek';

/** How long the mouse rests on a link before it is asking for the card rather than passing over it. */
const OPEN_DELAY_MS = 400;

const CARD_SELECTOR = '[data-peek-card]';

/**
 * Where a link draws no card: a surface that opted out, and any dialog. A press on the card is a press
 * outside a modal, which would close it and lose what was typed in it. A dialog that is no modal and closes on
 * nothing outside it opts its links back in with `data-peek="on"`; the nearer of these marks decides, and a
 * modal anywhere above the link still says no.
 */
const PEEK_MARK_SELECTOR = '[data-peek="on"], [data-peek="off"], [role="dialog"]';
const MODAL_SELECTOR = '[aria-modal="true"]';

/** Anchors in these roles are a listbox's, a menu's or a tab list's parts rather than links. */
const WIDGET_ROLES = new Set(['option', 'menuitem', 'tab']);

type RecordRef = { entityType: GlobalSearchEntityType; id: number };

type LinkPeek = RecordRef & { anchor: HTMLAnchorElement };

const recordKey = ({ entityType, id }: RecordRef) => `${entityType}:${id}`;

/**
 * The record the link under the pointer opens, when that link gets a card: an in-app link to a record other
 * than the page's own, which opens no popup of its own and is no widget's part.
 */
function peekableLinkAt(target: EventTarget | null, pageRecord: RecordRef | null): LinkPeek | null {
    const anchor = target instanceof Element ? target.closest('a[href]') : null;
    if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.origin !== window.location.origin ||
        anchor.hasAttribute('aria-haspopup') ||
        WIDGET_ROLES.has(anchor.getAttribute('role') ?? '') ||
        anchor.closest(MODAL_SELECTOR) ||
        anchor.closest(PEEK_MARK_SELECTOR)?.matches('[data-peek="on"]') === false
    ) {
        return null;
    }
    const record = resolveRecordFromPath(anchor.pathname);
    if (!record || (pageRecord && recordKey(pageRecord) === recordKey(record))) {
        return null;
    }
    return { ...record, anchor };
}

/** Whether the link sits in a part of the page that draws the new design on its own, as a Crespect AI surface does. */
const inScopedNewDesign = (anchor: Element) => {
    const scope = anchor.closest('[data-design="new"]');
    return scope !== null && scope !== document.documentElement;
};

/**
 * Hangs the search peek card off every record link on the page, whatever drew the link.
 *
 * One set of listeners on the document stands in for one per link, so no link primitive knows about the card.
 * Only the mouse raises it, as in search: a tap or a focus just follows the link. One card at a time, opened
 * when the pointer rests on a link and closed once it has left both the link and the card; a click, a page
 * change, Escape or the link leaving the page close it too.
 */
export function LinkPeekHost() {
    const location = useLocation();
    const [peek, setPeek] = useState<LinkPeek | null>(null);
    const hover = useRef<{
        pending: HTMLAnchorElement | null;
        openTimer?: ReturnType<typeof setTimeout>;
        closeTimer?: ReturnType<typeof setTimeout>;
    }>({ pending: null });
    // Records whose read failed: no card and no event for them again until the page is reloaded.
    const unavailable = useRef(new Set<string>());

    const close = useCallback(() => {
        clearTimeout(hover.current.closeTimer);
        hover.current.closeTimer = undefined;
        setPeek(null);
    }, []);

    /** The pointer came onto `target`, or left the window when it is `null`. */
    const track = useEffectEvent((target: EventTarget | null, pageRecord: RecordRef | null) => {
        const state = hover.current;
        const onCard = target instanceof Element && target.closest(CARD_SELECTOR) !== null;
        if (onCard || (target instanceof Node && peek?.anchor.contains(target))) {
            clearTimeout(state.closeTimer);
            state.closeTimer = undefined;
        } else if (peek) {
            state.closeTimer ??= setTimeout(close, CLOSE_DELAY_MS);
        }

        const link = onCard ? null : peekableLinkAt(target, pageRecord);
        const usable = link && !unavailable.current.has(recordKey(link)) ? link : null;
        // A move within the link it rests on keeps the wait running.
        if ((usable?.anchor ?? null) === state.pending) {
            return;
        }
        clearTimeout(state.openTimer);
        state.pending = usable?.anchor ?? null;
        if (usable && usable.anchor !== peek?.anchor) {
            state.openTimer = setTimeout(() => {
                if (!usable.anchor.isConnected) {
                    return;
                }
                captureSearchPeekOpened({ surface: 'link', entityType: usable.entityType });
                setPeek(usable);
            }, OPEN_DELAY_MS);
        }
    });

    // A press on the card's text leaves it up; a press on one of its buttons has done what it was for, and a
    // press anywhere else, the link included, is the pointer moving on.
    const press = useEffectEvent((target: EventTarget | null) => {
        clearTimeout(hover.current.openTimer);
        hover.current.pending = null;
        const onCardText =
            target instanceof Element && target.closest(CARD_SELECTOR) !== null && target.closest('button') === null;
        if (peek && !onCardText) {
            close();
        }
    });

    useEffect(() => {
        const pageRecord = resolveRecordFromPath(location.pathname);
        const state = hover.current;
        const onPointerOver = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                track(event.target, pageRecord);
            }
        };
        const onPointerOut = (event: PointerEvent) => {
            if (event.pointerType === 'mouse' && event.relatedTarget === null) {
                track(null, pageRecord);
            }
        };
        const onClick = (event: MouseEvent) => press(event.target);
        document.addEventListener('pointerover', onPointerOver);
        document.addEventListener('pointerout', onPointerOut);
        document.addEventListener('click', onClick);
        return () => {
            document.removeEventListener('pointerover', onPointerOver);
            document.removeEventListener('pointerout', onPointerOut);
            document.removeEventListener('click', onClick);
            clearTimeout(state.openTimer);
            state.pending = null;
            // Run on every navigation, which takes the card down with the page it was raised on.
            close();
        };
    }, [location, close]);

    useEffect(() => {
        if (!peek) {
            return undefined;
        }
        // The link wears its hover look while the pointer is on its card: a `data-peek-open:` style keys on this.
        peek.anchor.setAttribute('data-peek-open', '');
        // Nothing else notices a link that is re-rendered away under its card.
        const observer = new MutationObserver(() => {
            if (!peek.anchor.isConnected) {
                close();
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            peek.anchor.removeAttribute('data-peek-open');
        };
    }, [peek, close]);

    if (!peek) {
        return null;
    }
    const card = (
        // Where the layout hosts the create dialog, Log time opens it over this page and puts the card away here.
        <SearchCloseContext.Provider value={close}>
            {/* No trigger: Escape and a press outside the card reach `onOpenChange`. */}
            <Popover open onOpenChange={(open) => !open && close()}>
                <PeekPopup
                    anchor={peek.anchor}
                    entityType={peek.entityType}
                    recordId={peek.id}
                    onUnavailable={() => {
                        unavailable.current.add(recordKey(peek));
                        close();
                    }}
                />
            </Popover>
        </SearchCloseContext.Provider>
    );
    // A link on an AI surface raises its card through an AI portal container, like every overlay the surface opens,
    // so the card draws in the surface's tokens, in either design, above the floating panel.
    return inScopedNewDesign(peek.anchor) ? <AiSurface className="contents">{card}</AiSurface> : card;
}
